import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import Configurator from "../config/Configurator";
import { setAlarms } from "../services/slices/amsSlice";

dayjs.extend(utc);
dayjs.extend(timezone);

const useAlarmWebSocket = ({ isLoggedIn, liveFlag, isSliding }) => {
  const dispatch = useDispatch();
  const requestedSerials = useSelector((s) => s.alarm.requestedSerials);
  const prevSerialsRef = useRef([]);

  const live = liveFlag.current;

  const wsMapRef = useRef(new Map());
  const retryTimersRef = useRef(new Map());

  const requestedSerialsRef = useRef({});
  useEffect(() => {
    requestedSerialsRef.current = requestedSerials;
  }, [requestedSerials]);

  useEffect(() => {
    if (!isLoggedIn || !live || isSliding.current) {
      // Tear down everything only on full stop
      wsMapRef.current.forEach((ws, serial) => {
        ws?.close();
        clearTimeout(retryTimersRef.current.get(serial));
      });
      wsMapRef.current.clear();
      retryTimersRef.current.clear();
      prevSerialsRef.current = [];
      return;
    }

    const setupWebSocketForSerial = async (serial) => {
      if (wsMapRef.current.has(serial) && liveFlag.current) {
        const ws = wsMapRef.current.get(serial);
        if (
          ws.readyState !== WebSocket.CLOSED &&
          ws.readyState !== WebSocket.CLOSING
        ) {
          return;
        }
      }

      const config = await Configurator();
      const key = localStorage.getItem("apiKey");
      const user = localStorage.getItem("userID");
      if (!key || !user) return;

      const host = new URL(config.API_URL).host;
      const wsPrefix = config.API_WS_PREFIX;
      const url = `${wsPrefix}://${host}/api/v2/io/alarms/events/get/abstract/live`;

      const ws = new WebSocket(url);
      wsMapRef.current.set(serial, ws);

      ws.onopen = () => {
        ws.send(JSON.stringify({ data: { transmitterSerial: serial } }));
      };

      ws.onmessage = (evt) => {
        let d;
        try {
          d = JSON.parse(evt.data).data;
        } catch {
          return;
        }

        if (d.repeat || !liveFlag.current) return;
        const events = d.alarmEvents || [];
        dispatch({
          type: setAlarms.type,
          payload: { [serial]: events },
          meta: { mode: "merge" },
        });
      };

      ws.onclose = () => {
        const timer = setTimeout(() => {
          const currentSerials = Object.keys(requestedSerialsRef.current);
          const stillLive = liveFlag.current;

          if (!stillLive) {
            retryTimersRef.current.delete(serial);
            return;
          }

          if (currentSerials.includes(serial)) {
            setupWebSocketForSerial(serial);
          } else {
            retryTimersRef.current.delete(serial);
          }
        }, 5000);

        retryTimersRef.current.set(serial, timer);
      };

      ws.onerror = (err) => {
        console.error(`Alarm WS error for ${serial}`, err);
      };
    };

    const currentSerials = Object.keys(requestedSerialsRef.current);

    const prevSerials = prevSerialsRef.current;

    const added = currentSerials.filter((s) => !prevSerials.includes(s));
    const removed = prevSerials.filter((s) => !currentSerials.includes(s));

    prevSerialsRef.current = currentSerials;

    // Add new ones
    added.forEach((serial) => {
      setupWebSocketForSerial(serial);
    });

    // Remove closed ones
    removed.forEach((serial) => {
      const ws = wsMapRef.current.get(serial);
      ws?.close();
      clearTimeout(retryTimersRef.current.get(serial));
      wsMapRef.current.delete(serial);
      retryTimersRef.current.delete(serial);
    });

    // Only on unmount
    return () => {
      if (!isLoggedIn || !live || isSliding.current) {
        wsMapRef.current.forEach((ws, serial) => {
          ws?.close();
          clearTimeout(retryTimersRef.current.get(serial));
        });
        console.log("Alarm WS cleanup");
        dispatch(setAlarms({}));
        wsMapRef.current.clear();
        retryTimersRef.current.clear();
        prevSerialsRef.current = [];
      }
    };
  }, [isLoggedIn, live, isSliding.current, requestedSerials]);
};

export default useAlarmWebSocket;
